import {Platform} from 'react-native';
import {
  LESSON_VIEWER_FETCH_START,
  LESSON_VIEWER_FETCH_SUCCESS,
  LESSON_VIEWER_FETCH_ERROR,
  LESSON_VIEWER_RESET,
  LESSON_VIEWER_NARRATION_PENDING,
  LESSON_VIEWER_NARRATION_PLAYING,
  LESSON_VIEWER_NARRATION_COMPLETE,
  LESSON_VIEWER_NARRATION_ERROR,
  LESSON_VIEWER_NARRATION_RESET,
  LESSON_VIEWER_SET_PHASE,
  LESSON_VIEWER_ADVANCE_TERM,
  LESSON_VIEWER_SET_LEARNING_STEP_INDEX,
  LESSON_VIEWER_SET_WORKBOOK_EXERCISE_INDEX,
  LESSON_VIEWER_WORKBOOK_READY,
  LESSON_VIEWER_WORKBOOK_DONE,
  LESSON_VIEWER_WORKBOOK_SUBMIT,
  LESSON_VIEWER_KC_SUBMIT,
  LESSON_VIEWER_KC_NEXT,
  LESSON_VIEWER_KC_SUMMARY_DONE,
  LESSON_VIEWER_MASTERY_SUBMIT,
  LESSON_VIEWER_MASTERY_NEXT,
  LESSON_VIEWER_START_TERM_REVIEW,
  LESSON_VIEWER_RETAKE_MASTERY,
  LESSON_VIEWER_TOGGLE_DEV_TOOLS,
  LESSON_VIEWER_DEV_JUMP,
  LESSON_VIEWER_SET_TUTORIAL_VIEWED,
  LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  LESSON_VIEWER_UNITY_WORLD_LOADING_SET,
  LESSON_VIEWER_KEY_TERM_PROGRESS_SET,
} from './actionTypes';
import {
  fetchLessonContent as fetchFromApi,
  fetchLessonAudioManifest,
  getKeyTermProgress,
} from 'services/LessonContentService';
import {mapLessonContent} from 'services/LessonContentMapper';
import {
  clearTutorAudioCache,
  registerLessonAudioManifest,
} from 'services/TextToSpeechService';
import {selectActiveWorkbook} from './selectors';

const STORAGE_PREFIX = 'quiklearn_lcv_';

function getLessonAudioScope(lessonId) {
  return lessonId ? `lesson:${lessonId}` : 'lesson:unknown';
}

function getTutorialViewed(lessonId) {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${lessonId}_tutorial`) === 'true';
  } catch {
    return false;
  }
}

function setTutorialViewedStorage(lessonId) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${lessonId}_tutorial`, 'true');
  } catch {
    // silent
  }
}

function saveProgress(lessonId, progress) {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${lessonId}_progress`,
      JSON.stringify(progress),
    );
  } catch {
    // silent
  }
}

function saveTermCompletion(lessonId, termName) {
  try {
    const key = `${STORAGE_PREFIX}${lessonId}_termCompletion`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    if (!existing.includes(termName)) {
      existing.push(termName);
      localStorage.setItem(key, JSON.stringify(existing));
    }
  } catch {
    // silent
  }
}

export const fetchLessonContentAction = (lessonId, token, options = {}) => async dispatch => {
  dispatch({type: LESSON_VIEWER_FETCH_START});

  try {
    // Fetch lesson content and pre-rendered audio manifest in parallel so
    // the loading phase covers both. The viewer's UI must not render
    // until the manifest is settled (per FRONTEND_LESSON_AUDIO_FLOW.md):
    // a missing manifest is non-fatal — playback will fall back to the
    // per-clip download endpoint with `?autoGenerate=true`.
    const [rawPayload, manifestResult] = await Promise.all([
      fetchFromApi(lessonId, token),
      fetchLessonAudioManifest(lessonId, token).catch(error => {
        console.warn(
          '[lessonContentViewer] audio manifest fetch failed, will fall back to per-clip download:',
          error?.message || error,
        );
        return null;
      }),
    ]);

    const viewerModel = mapLessonContent(rawPayload);
    const tutorialViewed = getTutorialViewed(lessonId);

    // Always register a manifest record (even when null) so the TTS
    // service knows the lesson scope + lessonContentId/token and can
    // call the per-clip download endpoint as needed.
    registerLessonAudioManifest({
      lessonScope: getLessonAudioScope(lessonId),
      lessonContentId: lessonId,
      manifest: manifestResult,
      token,
    });

    dispatch({
      type: LESSON_VIEWER_FETCH_SUCCESS,
      payload: {
        rawPayload,
        viewerModel,
        tutorialViewed,
        unityWorldLoading: !options.skipWorld && Platform.OS === 'web',
      },
    });
  } catch (error) {
    dispatch({
      type: LESSON_VIEWER_FETCH_ERROR,
      payload: error.message || 'Failed to load lesson content',
    });
  }
};

export const resetViewer = () => (dispatch, getState) => {
  const lessonId =
    getState().lessonContentViewer.viewerModel?.metadata?.lessonId;
  clearTutorAudioCache(getLessonAudioScope(lessonId));
  dispatch({type: LESSON_VIEWER_RESET});
};

export const setNarrationPending = audioId => ({
  type: LESSON_VIEWER_NARRATION_PENDING,
  payload: {audioId},
});

export const setNarrationPlaying = audioId => ({
  type: LESSON_VIEWER_NARRATION_PLAYING,
  payload: {audioId},
});

export const setNarrationComplete = audioId => ({
  type: LESSON_VIEWER_NARRATION_COMPLETE,
  payload: {audioId},
});

export const setNarrationError = (audioId, error) => ({
  type: LESSON_VIEWER_NARRATION_ERROR,
  payload: {audioId, error},
});

export const resetNarration = () => ({
  type: LESSON_VIEWER_NARRATION_RESET,
});

export const setLessonViewerUnityWorldLoading = loading => ({
  type: LESSON_VIEWER_UNITY_WORLD_LOADING_SET,
  payload: Boolean(loading),
});

export const setPhase = phase => ({
  type: LESSON_VIEWER_SET_PHASE,
  payload: phase,
});

export const advanceTerm = termIndex => ({
  type: LESSON_VIEWER_ADVANCE_TERM,
  payload: termIndex,
});

export const setLearningStepIndex = stepIndex => ({
  type: LESSON_VIEWER_SET_LEARNING_STEP_INDEX,
  payload: stepIndex,
});

export const setWorkbookExerciseIndex = exerciseIndex => ({
  type: LESSON_VIEWER_SET_WORKBOOK_EXERCISE_INDEX,
  payload: exerciseIndex,
});

export const setTutorialViewed = () => (dispatch, getState) => {
  const lessonId =
    getState().lessonContentViewer.viewerModel?.metadata?.lessonId;
  if (lessonId) {
    setTutorialViewedStorage(lessonId);
  }
  dispatch({type: LESSON_VIEWER_SET_TUTORIAL_VIEWED});
};

export const setWorkbookReady = () => ({type: LESSON_VIEWER_WORKBOOK_READY});
export const setWorkbookDone = () => ({type: LESSON_VIEWER_WORKBOOK_DONE});

export const submitWorkbookAnswer = optionIndex => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const workbook = selectActiveWorkbook(getState());
  if (!workbook) return;

  const selectedAnswer = workbook.options?.[optionIndex];
  const correct = selectedAnswer === workbook.correctAnswer;
  const timestamp = Date.now();

  dispatch({
    type: LESSON_VIEWER_WORKBOOK_SUBMIT,
    payload: {optionIndex, selectedAnswer, correct, timestamp},
  });
};

export const advancePhase = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const lessonId = state.viewerModel?.metadata?.lessonId;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  const learningSteps = term?.learningSteps || [];
  const workbookExercises = term?.workbook?.exercises || [];

  let nextPhase = null;
  let nextLearningStepIndex = state.activeLearningStepIndex;
  let nextWorkbookExerciseIndex = state.activeWorkbookExerciseIndex;
  let nextQuestionIndex = 0;

  switch (state.phase) {
    case 'tutorial':
      nextPhase = 'frontloading';
      break;
    case 'frontloading':
      nextPhase = 'learning';
      nextLearningStepIndex = 0;
      nextWorkbookExerciseIndex = 0;
      break;
    case 'learning':
      if (state.activeLearningStepIndex + 1 < learningSteps.length) {
        nextPhase = 'learning';
        nextLearningStepIndex = state.activeLearningStepIndex + 1;
      } else {
        nextPhase = 'workbook_ready';
        nextWorkbookExerciseIndex = 0;
      }
      break;
    case 'workbook_ready':
      nextPhase = 'workbook_exercise';
      break;
    case 'workbook_exercise':
      nextPhase = 'workbook_options';
      break;
    case 'workbook_options':
      if (state.activeWorkbookExerciseIndex + 1 < workbookExercises.length) {
        nextPhase = 'workbook_ready';
        nextWorkbookExerciseIndex = state.activeWorkbookExerciseIndex + 1;
      } else {
        nextPhase = 'knowledge_check';
      }
      break;
    default:
      break;
  }

  if (!nextPhase) return;

  if (nextLearningStepIndex !== state.activeLearningStepIndex) {
    dispatch(setLearningStepIndex(nextLearningStepIndex));
  }

  if (nextWorkbookExerciseIndex !== state.activeWorkbookExerciseIndex) {
    dispatch(setWorkbookExerciseIndex(nextWorkbookExerciseIndex));
  }

  dispatch(setPhase(nextPhase));

  if (lessonId) {
    saveProgress(lessonId, {
      phase: nextPhase,
      activeTermIndex: state.activeTermIndex,
      activeQuestionIndex: nextQuestionIndex,
      activeLearningStepIndex: nextLearningStepIndex,
      activeWorkbookExerciseIndex: nextWorkbookExerciseIndex,
    });
  }
};

export const submitKCAnswer = selectedAnswer => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const question = term.knowledgeCheck.questions[state.activeQuestionIndex];
  if (!question) return;

  const correct = selectedAnswer === question.correctAnswer;
  const timestamp = Date.now();

  dispatch({
    type: LESSON_VIEWER_KC_SUBMIT,
    payload: {
      questionIndex: state.activeQuestionIndex,
      selectedAnswer,
      correct,
      timestamp,
    },
  });
};

export const advanceKCQuestion = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const totalQuestions = term.knowledgeCheck.questions.length;
  const nextIndex = state.activeQuestionIndex + 1;

  if (nextIndex < totalQuestions) {
    dispatch({type: LESSON_VIEWER_KC_NEXT});
  } else {
    dispatch(setPhase('knowledge_check_summary'));
  }
};

export const kcSummaryDone = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const lessonId = state.viewerModel?.metadata?.lessonId;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];

  if (term && lessonId) {
    saveTermCompletion(lessonId, term.term);
  }

  dispatch({type: LESSON_VIEWER_KC_SUMMARY_DONE});
};

export const submitMasteryAnswer = selectedAnswer => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const questions = state.viewerModel?.mastery?.questions || [];
  const question = questions[state.activeQuestionIndex];
  if (!question) return;

  const correct = selectedAnswer === question.correctAnswer;
  const timestamp = Date.now();

  dispatch({
    type: LESSON_VIEWER_MASTERY_SUBMIT,
    payload: {
      questionIndex: state.activeQuestionIndex,
      selectedAnswer,
      correct,
      timestamp,
    },
  });
};

export const advanceMasteryQuestion = () => ({
  type: LESSON_VIEWER_MASTERY_NEXT,
});

export const startTermReview = termIndex => ({
  type: LESSON_VIEWER_START_TERM_REVIEW,
  payload: termIndex,
});

export const retakeMasteryTest = () => ({
  type: LESSON_VIEWER_RETAKE_MASTERY,
});

export const toggleDevTools = () => ({type: LESSON_VIEWER_TOGGLE_DEV_TOOLS});

function buildMockAnswers(count, filledCount) {
  return Array.from({length: filledCount}, (_, index) => ({
    questionIndex: index,
    selectedAnswer: null,
    correct: Math.random() > 0.35,
  }));
}

export const jumpToDevTimelineNode = node => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const model = state.viewerModel;
  if (!model) return;

  const masteryQuestionCount = model.mastery?.questions?.length || 0;
  const kcQuestionCount =
    model.terms?.[node.termIndex]?.knowledgeCheck?.questions?.length || 0;

  const payload = {
    tutorialViewed: node.phase !== 'tutorial',
    phase: node.phase,
    activeTermIndex: node.termIndex ?? 0,
    activeQuestionIndex: 0,
    workbookReady: false,
    workbookDone: false,
    workbookSubmitted: false,
    workbookSelectedOption: null,
    kcAnswers: [],
    masteryAnswers: [],
    masteryComplete: false,
  };

  if (node.phase === 'workbook_ready') {
    payload.workbookReady = false;
  }

  if (node.phase === 'knowledge_check_summary') {
    payload.kcAnswers = buildMockAnswers(kcQuestionCount, kcQuestionCount);
  }

  if (node.phase === 'mastery') {
    payload.activeQuestionIndex = node.masteryQuestionIndex || 0;
    payload.masteryAnswers = buildMockAnswers(
      masteryQuestionCount,
      node.masteryQuestionIndex || 0,
    );
  }

  if (node.phase === 'results') {
    payload.activeQuestionIndex = Math.max(0, masteryQuestionCount - 1);
    payload.masteryAnswers = buildMockAnswers(
      masteryQuestionCount,
      masteryQuestionCount,
    );
    payload.masteryComplete = true;
  }

  dispatch({
    type: LESSON_VIEWER_DEV_JUMP,
    payload,
  });
};

function resolveResumePhaseAndTerm(terms, lastTerm, status) {
  if (!Array.isArray(terms) || terms.length === 0) return null;

  const termIndex = terms.findIndex(t => t?.term === lastTerm);
  if (termIndex < 0) return null;

  switch (status) {
    case 'not_started':
    case 'learning':
      return {phase: 'learning', termIndex};
    case 'workbook':
      return {phase: 'workbook_ready', termIndex};
    case 'knowledge_check':
      return {phase: 'knowledge_check', termIndex};
    case 'completed': {
      const nextTermIndex = termIndex + 1;
      if (nextTermIndex < terms.length) {
        return {phase: 'learning', termIndex: nextTermIndex};
      }
      return {phase: 'mastery', termIndex};
    }
    default:
      return null;
  }
}

export const resumeViewerFromBackend = currentState => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const model = state.viewerModel;
  if (!model) return;

  const lastTerm = currentState?.lastTerm;
  const status = currentState?.status;
  if (!lastTerm || !status) return;

  const resolved = resolveResumePhaseAndTerm(model.terms, lastTerm, status);
  if (!resolved) return;

  const masteryQuestionCount = model.mastery?.questions?.length || 0;

  const payload = {
    tutorialViewed: true,
    phase: resolved.phase,
    activeTermIndex: resolved.termIndex,
    activeQuestionIndex: 0,
    workbookReady: false,
    workbookDone: false,
    workbookSubmitted: false,
    workbookSelectedOption: null,
    kcAnswers: [],
    masteryAnswers: [],
    masteryComplete: false,
  };

  if (resolved.phase === 'mastery') {
    payload.activeQuestionIndex = 0;
  }

  dispatch({
    type: LESSON_VIEWER_DEV_JUMP,
    payload,
  });
};

export const setTutorChatOpen = open => ({
  type: LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  payload: open,
});

/**
 * Replace the persistent per-key-term progress map. `entries` may be the
 * raw array returned by GET /key-term-progress or an already-built
 * `{term: status}` object; both shapes are normalized to lowercased term
 * keys here.
 */
export const setKeyTermProgress = entries => {
  let map = {};
  if (Array.isArray(entries)) {
    map = entries.reduce((acc, row) => {
      const term = String(row?.term || '').trim().toLowerCase();
      if (term && row?.status) acc[term] = row.status;
      return acc;
    }, {});
  } else if (entries && typeof entries === 'object') {
    map = Object.keys(entries).reduce((acc, key) => {
      const term = String(key || '').trim().toLowerCase();
      const status = entries[key];
      if (term && status) acc[term] = status;
      return acc;
    }, {});
  }
  return {type: LESSON_VIEWER_KEY_TERM_PROGRESS_SET, payload: map};
};

export const fetchKeyTermProgressAction =
  (lessonId, token) => async dispatch => {
    if (!lessonId || !token) return;
    const rows = await getKeyTermProgress(lessonId, token);
    dispatch(setKeyTermProgress(rows));
  };

/**
 * Navigate the viewer to a (termIndex, phase) pair clicked from the
 * progress tracker. Reuses the dev-jump reducer because it already resets
 * per-term and per-question fields cleanly. We deliberately don't PUT
 * /key-term-progress here — the learner is *re-visiting* a cleared
 * section and the existing persisted status (from prior runs) already
 * represents the high-water mark; overwriting with a lower status would
 * regress their record.
 */
export const navigateToTrackerNode =
  ({termIndex, phase}) =>
  (dispatch, getState) => {
    if (typeof termIndex !== 'number' || !phase) return;
    const state = getState().lessonContentViewer;
    const model = state.viewerModel;
    const totalTerms = model?.terms?.length || 0;
    if (!model || termIndex < 0 || termIndex >= totalTerms) return;

    dispatch({
      type: LESSON_VIEWER_DEV_JUMP,
      payload: {
        tutorialViewed: true,
        phase,
        activeTermIndex: termIndex,
        activeQuestionIndex: 0,
        workbookReady: false,
        workbookDone: false,
        workbookSubmitted: false,
        workbookSelectedOption: null,
        kcAnswers: [],
        masteryAnswers: [],
        masteryComplete: false,
      },
    });
  };

import World from "components/World";
let speechTimer = null;
const DEFAULT_ID = "manual_speech_123";
const PLACEHOLDER_TEXT = "speaking_animation_active";

/**
 * Starts the lip-sync animation for the tutor.
 * @param {number} [duration] - Optional time in milliseconds before auto-stopping.
 */
export const startSpeaking = (duration) => {
  // Clear any existing timer to prevent overlapping stops
  if (speechTimer) {
    clearTimeout(speechTimer);
    speechTimer = null;
  }

  // Trigger the 'interim' command to start/keep animation going
  World.runCommand(
    `livekit_final_response ${DEFAULT_ID} ${PLACEHOLDER_TEXT} false tutor`
  );

  // If a duration is provided, set a timeout to stop speaking
  if (duration && typeof duration === "number") {
    speechTimer = setTimeout(() => {
      stopSpeaking();
    }, duration);
  }
};

/**
 * Stops the lip-sync animation immediately.
 */
export const stopSpeaking = () => {
  if (speechTimer) {
    clearTimeout(speechTimer);
    speechTimer = null;
  }

  // Trigger the 'final' command to stop the animation
  World.runCommand(
    `livekit_final_response ${DEFAULT_ID} ${PLACEHOLDER_TEXT} true tutor`
  );
};
